/* Peggy Bakes — accessibility fixes (WCAG 2.2 AA), October 2026.

   SEARCH PANEL
   1. Tab and Shift+Tab move normally through the search panel (search box, Clear, Close,
      each suggestion, "View all") instead of being captured. Up and down arrows still move through suggestions.
   2. Screen readers hear which suggestion is highlighted when the arrow keys are used
      (the theme already announces how many results were found).
   3. The search box reports when its suggestion list is open (aria-expanded).

   CART PAGE
   4. The small "10% OFF" tag picture in Recurpay's "Switch to subscription" box is marked
      as decoration (the words "10% OFF" right beside it are what get read).
   5. The cart table's hidden caption loses a stray role that breaks the table for
      screen readers.

   SHOP PAGES (product cards and filters)
   6. Each card's photo arrows say which product they belong to
      ("Next photo of Cocoa Noir Granola" instead of just "Next slide").
   7. The "Add" and "Choose" buttons say which product they're for
      ("Add Granola Sampler to cart", "Choose options for Cocoa Noir Granola").
   8. The filter list is marked up as a proper list for screen readers.

   HOME PAGE
   9. The scrolling "Dairy Free · Gluten Free · No Added Refined Sugar" banner gets a
      pause / play button, and its words are read once by screen readers.

   HEADINGS (all pages)
   10. Screen readers get a tidy page outline with no skipped levels. Headings that jump a
       level (for example the "The bright one" line under a product name, "Ingredients" on
       recipe pages, product names on shop pages) are announced one level down from the
       heading above them. Nothing changes on screen.
   11. Pages with no main heading (search results, the Timeline page, the box builder)
       get one that only screen readers hear.

   PRODUCT PAGES
   12. The dots under the product photos (phones) say which photo is showing in a way screen
       readers understand ("current"), instead of a setting buttons aren't allowed to use.
   13. The grey boxes shown while "You may also like" loads are hidden from screen readers
       (they carried a "Loading" label that screen readers can't read on a plain box).

   INSTAGRAM FEED (bottom of most pages)
   14. A "Skip past Instagram feed" link appears when a keyboard user reaches the feed, so they
       don't have to Tab through about 28 stops to get to the footer.
   15. "Like", "Comment" and "Open post" say which post they belong to ("Like post 3 on
       Instagram"), and the feed's header is no longer marked as a second page banner.
       The "peggybakesco" and "Peggy Bakes" name links in the header are 24 px tall.

   BUILD A BOX (Recurpay's box builder, /apps/recurpay/bundles/...)
   16. The browser tab reads "Build a Box – Peggy Bakes" instead of just "Peggy Bakes".
   17. When the box is full and Recurpay redraws the bottom tray, each bag picture gets its
       flavor name again, and each × becomes a keyboard-reachable "Remove <flavor>" button
       (Enter or Space removes the bag), 24 x 24 px. After a bag is removed with the
       keyboard, the keyboard lands on the next remove button instead of the top of the page.
   18. The Checkout button is marked unavailable for screen readers whenever Recurpay shows
       it greyed out (including the empty box).
   19. When a box moves up a size (a third bag in a 2-bag box), screen readers hear
       "3 of 3 bags chosen" instead of "3 of 2 bags chosen".
   (The crossed-out regular price is darkened in pb-a11y.css.) */
(function () {
  'use strict';

  var ITEM_SELECTOR = '[ref="resultsItems[]"], .predictive-search-results__card';

  function inputOf(root) {
    return root.querySelector('input[role="combobox"]');
  }

  function items(root) {
    var list = root.querySelectorAll(ITEM_SELECTOR);
    var seen = [];
    for (var i = 0; i < list.length; i++) {
      if (seen.indexOf(list[i]) === -1) seen.push(list[i]);
    }
    return seen;
  }

  function labelOf(item) {
    var link = item.querySelector('a');
    var text = '';
    if (link) text = link.getAttribute('aria-label') || '';
    if (!text) {
      var title = item.querySelector('.product-card__title, .resource-card__title, h3, h4, [class*="title"]');
      text = (title || link || item).textContent || '';
    }
    return text.replace(/\s+/g, ' ').trim().slice(0, 120);
  }

  function setup(root) {
    if (root.dataset.pbA11y === '1') return;
    var input = inputOf(root);
    if (!input) return;
    root.dataset.pbA11y = '1';

    var status = document.createElement('div');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    status.style.cssText =
      'position:absolute!important;width:1px!important;height:1px!important;padding:0!important;margin:-1px!important;overflow:hidden!important;clip:rect(0,0,0,0)!important;white-space:nowrap!important;border:0!important;';
    root.appendChild(status);

    var lastSpoken = '';
    function say(text) {
      if (text === lastSpoken) text += ' ';
      lastSpoken = text;
      status.textContent = '';
      window.setTimeout(function () { status.textContent = text; }, 50);
    }

    var countTimer = null;
    function resultsChanged() {
      window.clearTimeout(countTimer);
      countTimer = window.setTimeout(function () {
        var query = (input.value || '').trim();
        var n = items(root).length;
        input.setAttribute('aria-expanded', query && n ? 'true' : 'false');
      }, 600);
    }

    function highlightChanged() {
      var all = items(root);
      for (var i = 0; i < all.length; i++) {
        if (all[i].getAttribute('aria-selected') === 'true') {
          say(labelOf(all[i]) + ', ' + (i + 1) + ' of ' + all.length);
          return;
        }
      }
    }

    new MutationObserver(function (records) {
      var structural = false, highlight = false;
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        if (r.target === status || status.contains(r.target)) continue;
        if (r.type === 'attributes' && r.attributeName === 'aria-selected') highlight = true;
        else if (r.type === 'childList') structural = true;
      }
      if (structural) resultsChanged();
      if (highlight) highlightChanged();
    }).observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-selected'] });

    input.addEventListener('input', resultsChanged);
  }

  /* Let Tab move focus normally out of the search box. This listener runs first
     (capture phase on the window, ahead of the theme's own listeners) and stops the theme's handler from turning Tab
     into "highlight next suggestion". The browser's normal Tab still happens. */
  window.addEventListener('keydown', function (event) {
    if (event.key !== 'Tab') return;
    var t = event.target;
    if (t && t.closest && t.closest('predictive-search-component')) {
      event.stopPropagation();
    }
  }, true);

  function fixCart() {
    var tags = document.querySelectorAll('img.discount-tag-img:not([alt])');
    for (var i = 0; i < tags.length; i++) tags[i].setAttribute('alt', '');
    var captions = document.querySelectorAll('table.cart-items__table > caption[role]');
    for (var j = 0; j < captions.length; j++) captions[j].removeAttribute('role');
  }

  function nameOfCard(card) {
    var link = card.querySelector('a[ref="cardGalleryLink"][aria-label]');
    var text = link ? link.getAttribute('aria-label') : '';
    if (!text) {
      var title = card.querySelector('[role="heading"], .product-card__title, a.contents');
      text = title ? title.textContent : '';
    }
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  function label(el, text) {
    if (el && text && el.getAttribute('aria-label') !== text) el.setAttribute('aria-label', text);
  }

  function fixCards() {
    var cards = document.querySelectorAll('product-card');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var name = nameOfCard(card);
      if (!name) continue;
      label(card.querySelector('.slideshow-control--previous'), 'Previous photo of ' + name);
      label(card.querySelector('.slideshow-control--next'), 'Next photo of ' + name);
      label(card.querySelector('.quick-add__button--add'), 'Add ' + name + ' to cart');
      label(card.querySelector('.quick-add__button--choose'), 'Choose options for ' + name);
    }
    var facets = document.querySelectorAll('overflow-list.facets__overflow-list > accordion-custom:not([role])');
    for (var j = 0; j < facets.length; j++) facets[j].setAttribute('role', 'listitem');
  }

  var PAUSE_ICON = '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20" width="18" height="18"><rect x="4" y="3" width="4" height="14" rx="1" fill="currentColor"/><rect x="12" y="3" width="4" height="14" rx="1" fill="currentColor"/></svg>';
  var PLAY_ICON = '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20" width="18" height="18"><path d="M6 3.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L7.5 2.64A1 1 0 0 0 6 3.5z" fill="currentColor"/></svg>';

  function fixMarquee() {
    var bands = document.querySelectorAll('.pbh-marquee:not([data-pb-pause])');
    for (var i = 0; i < bands.length; i++) {
      (function (band) {
        band.setAttribute('data-pb-pause', '1');
        var words = band.getAttribute('aria-label');
        if (words) {
          band.removeAttribute('aria-label');
          var sr = document.createElement('span');
          sr.className = 'pb-visually-hidden';
          sr.textContent = words;
          band.insertBefore(sr, band.firstChild);
        }
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pb-marquee-toggle';
        function show(paused) {
          band.classList.toggle('pb-paused', paused);
          btn.setAttribute('aria-label', paused ? 'Play the scrolling banner' : 'Pause the scrolling banner');
          btn.innerHTML = paused ? PLAY_ICON : PAUSE_ICON;
        }
        btn.addEventListener('click', function () { show(!band.classList.contains('pb-paused')); });
        show(false);
        band.appendChild(btn);
      })(bands[i]);
    }
  }

  function visibleHeadings(root) {
    var list = root.querySelectorAll('h1, h2, h3, h4, h5, h6, [role="heading"]');
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var h = list[i];
      if (h.closest('[aria-hidden="true"], dialog:not([open]), template')) continue;
      if (h.checkVisibility && !h.checkVisibility({ visibilityProperty: true })) continue;
      out.push(h);
    }
    return out;
  }

  function originalLevel(h) {
    if (!h.hasAttribute('data-pb-level')) {
      var aria = h.getAttribute('aria-level');
      var lvl = aria || (/^H[1-6]$/.test(h.tagName) ? h.tagName.charAt(1) : '2');
      h.setAttribute('data-pb-level', lvl);
      h.setAttribute('data-pb-aria', aria || '');
    }
    return parseInt(h.getAttribute('data-pb-level'), 10);
  }

  function fixHeadings() {
    var main = document.querySelector('main') || document.body;
    var hs = visibleHeadings(main);
    var all = visibleHeadings(document.body);
    var hasH1 = false;
    for (var k = 0; k < all.length; k++) if (originalLevel(all[k]) === 1) hasH1 = true;

    if (!hasH1 && !main.querySelector('.pb-added-h1')) {
      var path = location.pathname;
      var text = path.indexOf('/search') === 0 ? 'Search results'
        : path.indexOf('/apps/recurpay/bundles') === 0 ? 'Build your box'
        : (document.title || '').split(/ [–—|-] /)[0].trim();
      if (text) {
        var h1 = document.createElement('h1');
        h1.className = 'pb-added-h1 pb-visually-hidden';
        h1.textContent = text;
        main.insertBefore(h1, main.firstChild);
        hs.unshift(h1);
      }
    }

    /* The page's main heading may sit outside <main> (on the home page it is the logo),
       so start the outline under it. */
    var stack = [{ orig: 1, level: 1 }];
    for (var i = 0; i < hs.length; i++) {
      var h = hs[i];
      var orig = originalLevel(h);
      while (stack.length && stack[stack.length - 1].orig >= orig) stack.pop();
      var parentLevel = stack.length ? stack[stack.length - 1].level : 0;
      var level = Math.min(orig, parentLevel + 1);
      if (level < 1) level = 1;
      if (level !== orig) {
        if (h.getAttribute('aria-level') !== String(level)) h.setAttribute('aria-level', String(level));
      } else {
        var had = h.getAttribute('data-pb-aria');
        if (had) {
          if (h.getAttribute('aria-level') !== had) h.setAttribute('aria-level', had);
        } else if (h.hasAttribute('aria-level')) {
          h.removeAttribute('aria-level');
        }
      }
      stack.push({ orig: orig, level: level });
    }
  }

  function fixDots() {
    var dots = document.querySelectorAll('.slideshow-controls__dots button[aria-selected]');
    for (var i = 0; i < dots.length; i++) {
      var d = dots[i];
      if (d.getAttribute('aria-selected') === 'true') d.setAttribute('aria-current', 'true');
      else d.removeAttribute('aria-current');
      d.removeAttribute('aria-selected');
    }
  }

  function fixSkeletons() {
    var boxes = document.querySelectorAll('.product-recommendations__skeleton-item[aria-label]');
    for (var i = 0; i < boxes.length; i++) {
      boxes[i].removeAttribute('aria-label');
      boxes[i].setAttribute('aria-hidden', 'true');
    }
  }

  var igWatched = [];

  function fixInstagram() {
    var feeds = document.querySelectorAll('.pb-ig');
    for (var i = 0; i < feeds.length; i++) {
      var feed = feeds[i];
      if (!feed.hasAttribute('data-pb-skip')) {
        feed.setAttribute('data-pb-skip', '1');
        var id = 'pb-after-ig-' + i;
        var skip = document.createElement('a');
        skip.className = 'pb-skip-link';
        skip.href = '#' + id;
        skip.textContent = 'Skip past Instagram feed';
        var end = document.createElement('span');
        end.id = id;
        end.setAttribute('tabindex', '-1');
        feed.parentNode.insertBefore(skip, feed);
        feed.parentNode.insertBefore(end, feed.nextSibling);
      }
    }

    var hosts = document.querySelectorAll('[class*="eapps-instagram-feed"]');
    for (var h = 0; h < hosts.length; h++) {
      var sr = hosts[h].shadowRoot;
      if (!sr) continue;
      if (!sr.querySelector('style[data-pb-a11y]')) {
        /* The "peggybakesco" and "Peggy Bakes" name links in the feed header: 24 px tall
           tap areas (were 22 and 18 px). */
        var css = document.createElement('style');
        css.setAttribute('data-pb-a11y', '');
        css.textContent = '.es-header-account-author-username-link,.es-header-account-author-name-link{min-height:24px!important;line-height:24px!important;}';
        sr.appendChild(css);
      }
      if (igWatched.indexOf(sr) === -1) {
        igWatched.push(sr);
        new MutationObserver(queueScan).observe(sr, { childList: true, subtree: true });
      }
      var banners = sr.querySelectorAll('[role="banner"]');
      for (var b = 0; b < banners.length; b++) banners[b].setAttribute('role', 'group');
      var regions = sr.querySelectorAll('[role="region"][aria-label="Carousel"]');
      for (var g = 0; g < regions.length; g++) regions[g].setAttribute('aria-label', 'Instagram posts');

      var opens = Array.prototype.slice.call(sr.querySelectorAll('button.es-post-keyboard-open-control'));
      for (var o = 0; o < opens.length; o++) label(opens[o], 'Open post ' + (o + 1) + ' from Instagram');
      var acts = sr.querySelectorAll('a.es-card-actions-action-link');
      for (var a = 0; a < acts.length; a++) {
        var link = acts[a];
        if (!link.hasAttribute('data-pb-orig')) link.setAttribute('data-pb-orig', link.getAttribute('aria-label') || '');
        var kind = link.getAttribute('data-pb-orig');
        var n = 0, node = link.parentElement;
        while (node && node !== sr && !n) {
          var btn = node.querySelector ? node.querySelector('button.es-post-keyboard-open-control') : null;
          if (btn) n = opens.indexOf(btn) + 1;
          node = node.parentElement;
        }
        if (!n) continue;
        if (kind === 'Like') label(link, 'Like post ' + n + ' on Instagram');
        else if (kind === 'Comment') label(link, 'Comment on post ' + n + ' on Instagram');
      }
    }
  }

  /* Recurpay's Build a Box page. Recurpay redraws its tray and Checkout button often,
     so these run again after every change. */
  var onBox = /^\/apps\/recurpay\/bundles\//.test(window.location.pathname);
  var BOX_TITLE = 'Build a Box – Peggy Bakes';
  function pressWithKeys(event) {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this.click();
    }
  }
  function fixRecurpayBox() {
    if (!onBox) return;
    if (document.title !== BOX_TITLE) document.title = BOX_TITLE;
    var tray = document.getElementById('update-img-list');
    var slots = tray ? tray.querySelectorAll('.update-img') : [];
    for (var i = 0; i < slots.length; i++) {
      var tip = slots[i].querySelector('.tooltiptext');
      var name = tip ? tip.textContent.trim() : '';
      var img = slots[i].querySelector('img');
      if (img && !img.hasAttribute('alt')) img.setAttribute('alt', name);
      var x = slots[i].querySelector('span.delete-cart-item');
      if (x && !x.hasAttribute('data-pb-remove')) {
        x.setAttribute('data-pb-remove', '');
        x.setAttribute('role', 'button');
        x.tabIndex = 0;
        x.setAttribute('aria-label', name ? 'Remove ' + name : 'Remove bag');
        x.addEventListener('keydown', pressWithKeys);
      }
    }
    var cos = document.querySelectorAll('[data-testid="bottom-cart-hud"]');
    for (var j = 0; j < cos.length; j++) {
      var co = cos[j];
      var off = co.disabled || co.classList.contains('disabled');
      if (off && co.getAttribute('aria-disabled') !== 'true') co.setAttribute('aria-disabled', 'true');
      else if (!off && co.getAttribute('aria-disabled') === 'true') co.removeAttribute('aria-disabled');
      if (!co.pbWatched) {
        co.pbWatched = true;
        new MutationObserver(fixRecurpayBox).observe(co, { attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled'] });
      }
    }
    var st = document.getElementById('recurpay-byob-selection-status');
    var m = st ? /^(\d+) of (\d+) bags chosen$/.exec(st.textContent.trim()) : null;
    if (m && +m[1] > +m[2]) {
      st.textContent = m[1] + ' of ' + Math.max(+m[1], slots.length) + ' bags chosen';
    }
  }

  /* Removing a bag with the keyboard redraws the tray and drops the keyboard at the top of
     the page. Put it back on the next bag's remove button, or on the first ADD button. */
  function restoreBoxFocus() {
    var a = document.activeElement;
    if (a && a !== document.body && document.contains(a)) return;
    var next = document.querySelector('#update-img-list .delete-cart-item[aria-label]') ||
      document.querySelector('#recurpay_product_cards button[aria-label^="Add "]');
    if (next) next.focus();
  }
  document.addEventListener('click', function (event) {
    if (!onBox || event.detail !== 0 || !event.target.closest) return;
    if (!event.target.closest('#update-img-list .delete-cart-item')) return;
    window.setTimeout(restoreBoxFocus, 400);
    window.setTimeout(restoreBoxFocus, 1200);
  }, true);

  function scan() {
    var roots = document.querySelectorAll('predictive-search-component');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
    fixCart();
    fixCards();
    fixMarquee();
    fixHeadings();
    fixDots();
    fixSkeletons();
    fixInstagram();
    fixRecurpayBox();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
  var scanQueued = false;
  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    window.requestAnimationFrame(function () { scanQueued = false; scan(); });
  }
  new MutationObserver(queueScan).observe(document.documentElement, { childList: true, subtree: true });

  /* The theme re-marks the current photo dot every time the photo changes. */
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      if (records[i].target.getAttribute('aria-selected') !== null) { fixDots(); return; }
    }
  }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['aria-selected'] });
})();
